"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { ArrowLeft, Edit2, GraduationCap, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { EducationData, EducationItem } from "../types";
import { normalizeEducation } from "../utils";
import { ProfileStepsLayout, Step } from "../components/ProfileStepsLayout";

interface TalentEducationProps {
  currentStep: number;
  totalSteps: number;
}

export default function TalentEducation({
  currentStep,
  totalSteps
}: TalentEducationProps) {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [educationItems, setEducationItems] = useState<EducationItem[]>([]);
  const [initialEducationItems, setInitialEducationItems] = useState<
    EducationItem[]
  >([]);

  // Form State for new education item
  const [newEducation, setNewEducation] = useState<Partial<EducationItem>>({
    institution: "",
    degree: "",
    fieldOfStudy: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    description: ""
  });
  const [editingId, setEditingId] = useState<string | null>(null);

  // Step information
  const stepTitle = t("talent:education.title");
  const stepDescription = t("talent:education.description");
  const estimatedTime = t("talent:application.estimated_times.5_minutes");

  const steps: Step[] = [
    {
      id: "personal_info",
      title: t("talent:layout.progress_steps.personal_info"),
      href: "/profile/personal-info",
      status: "completed"
    },
    {
      id: "german_skills",
      title: t("talent:dashboard.progress.steps.german_skills"),
      href: "/profile/german-skills",
      status: "completed"
    },
    {
      id: "experience",
      title: t("talent:dashboard.progress.steps.experience"),
      href: "/profile/experience",
      status: "completed"
    },
    {
      id: "education",
      title: t("talent:dashboard.progress.steps.education"),
      href: "/profile/education",
      status: "current"
    }
  ];

  const lastFetchedProfileId = useRef<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profile?.id) return;
      if (lastFetchedProfileId.current === profile.id) return;

      try {
        lastFetchedProfileId.current = profile.id;
        const { data, error } = await supabase
          .from("talent_profiles")
          .select("education")
          .eq("id", profile.id)
          .single();

        if (error) throw error;

        if (data?.education) {
          const normalized = normalizeEducation(data.education);
          setEducationItems(normalized.entries);
          setInitialEducationItems(normalized.entries);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast({
          title: t("common:status.error"),
          description: t("talent:profile.load_error"),
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profile?.id, t, toast]);

  const validateNewEducation = () => {
    if (
      !newEducation.institution ||
      !newEducation.degree ||
      !newEducation.fieldOfStudy ||
      !newEducation.startDate ||
      (!newEducation.isCurrent && !newEducation.endDate)
      // Description is optional for education often, but let's keep it consistent
    ) {
      return false;
    }
    return true;
  };

  const handleAddEducation = () => {
    if (!validateNewEducation()) {
      toast({
        title: t("common:status.error"),
        description: t("talent:education.validation_error"),
        variant: "destructive"
      });
      return;
    }

    if (editingId) {
      // Update existing
      setEducationItems((prev) =>
        prev.map((item) =>
          item.id === editingId
            ? {
                ...item,
                institution: newEducation.institution!,
                degree: newEducation.degree!,
                fieldOfStudy: newEducation.fieldOfStudy!,
                startDate: newEducation.startDate!,
                endDate: newEducation.isCurrent ? null : newEducation.endDate!,
                isCurrent: newEducation.isCurrent || false,
                description: newEducation.description || ""
              }
            : item
        )
      );
      setEditingId(null);
    } else {
      // Add new
      const item: EducationItem = {
        id: crypto.randomUUID(),
        institution: newEducation.institution!,
        degree: newEducation.degree!,
        fieldOfStudy: newEducation.fieldOfStudy!,
        startDate: newEducation.startDate!,
        endDate: newEducation.isCurrent ? null : newEducation.endDate!,
        isCurrent: newEducation.isCurrent || false,
        description: newEducation.description || ""
      };
      setEducationItems([...educationItems, item]);
    }

    setNewEducation({
      institution: "",
      degree: "",
      fieldOfStudy: "",
      startDate: "",
      endDate: "",
      isCurrent: false,
      description: ""
    });
  };

  const handleEdit = (item: EducationItem) => {
    setNewEducation({
      institution: item.institution,
      degree: item.degree,
      fieldOfStudy: item.fieldOfStudy,
      startDate: item.startDate,
      endDate: item.endDate || "",
      isCurrent: item.isCurrent,
      description: item.description
    });
    setEditingId(item.id);
  };

  const handleCancelEdit = () => {
    setNewEducation({
      institution: "",
      degree: "",
      fieldOfStudy: "",
      startDate: "",
      endDate: "",
      isCurrent: false,
      description: ""
    });
    setEditingId(null);
  };

  const handleRemoveEducation = (id: string) => {
    if (editingId === id) {
      handleCancelEdit();
    }
    setEducationItems(educationItems.filter((item) => item.id !== id));
  };

  const handleSave = async () => {
    const finalItems = [...educationItems];

    // Check if form is dirty
    const isFormDirty =
      newEducation.institution ||
      newEducation.degree ||
      newEducation.fieldOfStudy ||
      newEducation.startDate;

    if (isFormDirty) {
      if (validateNewEducation()) {
        // Save form data into list
        if (editingId) {
          const index = finalItems.findIndex((item) => item.id === editingId);
          if (index !== -1) {
            finalItems[index] = {
              ...finalItems[index],
              institution: newEducation.institution!,
              degree: newEducation.degree!,
              fieldOfStudy: newEducation.fieldOfStudy!,
              startDate: newEducation.startDate!,
              endDate: newEducation.isCurrent ? null : newEducation.endDate!,
              isCurrent: newEducation.isCurrent || false,
              description: newEducation.description || ""
            };
          }
        } else {
          finalItems.push({
            id: crypto.randomUUID(),
            institution: newEducation.institution!,
            degree: newEducation.degree!,
            fieldOfStudy: newEducation.fieldOfStudy!,
            startDate: newEducation.startDate!,
            endDate: newEducation.isCurrent ? null : newEducation.endDate!,
            isCurrent: newEducation.isCurrent || false,
            description: newEducation.description || ""
          });
        }
        handleCancelEdit();
      } else {
        toast({
          title: t("common:status.warning"),
          description: t("talent:education.validation_error"),
          variant: "default"
        });
        return;
      }
    }

    if (JSON.stringify(finalItems) === JSON.stringify(initialEducationItems)) {
      navigate("/profile");
      return;
    }

    setSaving(true);
    try {
      const payload: EducationData = {
        entries: finalItems
      };

      const { error } = await supabase
        .from("talent_profiles")
        .update({
          education: payload
        })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: t("common:status.success"),
        description: t("talent:education.save_success")
      });

      navigate("/profile");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: t("common:status.error"),
        description: t("talent:education.save_error"),
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <ProfileStepsLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={stepTitle}
      description={stepDescription}
      estimatedTime={estimatedTime}
      steps={steps}
    >
      <div className="space-y-6">
        {/* List of Added Education */}
        {educationItems.length > 0 && (
          <div className="space-y-4">
            {educationItems.map((item) => (
              <Card key={item.id} data-testid={`education-item-${item.id}`}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      {item.institution}
                    </CardTitle>
                    <CardDescription className="font-medium text-gray-700">
                      {item.degree} - {item.fieldOfStudy}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(item)}
                      className="text-muted-foreground hover:text-primary"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      id={`remove-education-${item.id}`}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveEducation(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-gray-500 mb-2">
                    {new Date(item.startDate).toLocaleDateString()} -{" "}
                    {item.isCurrent
                      ? t("talent:experience.present")
                      : item.endDate
                        ? new Date(item.endDate).toLocaleDateString()
                        : ""}
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {item.description}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add New Education Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              {editingId
                ? t("talent:education.edit_title", "Edit Education")
                : t("talent:education.add_new_title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="institution">
                {t("talent:education.fields.institution")} *
              </Label>
              <Input
                id="institution"
                value={newEducation.institution}
                onChange={(e) =>
                  setNewEducation({
                    ...newEducation,
                    institution: e.target.value
                  })
                }
                placeholder={t("talent:education.placeholders.institution")}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="degree">
                  {t("talent:education.fields.degree")} *
                </Label>
                <Input
                  id="degree"
                  value={newEducation.degree}
                  onChange={(e) =>
                    setNewEducation({ ...newEducation, degree: e.target.value })
                  }
                  placeholder={t("talent:education.placeholders.degree")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fieldOfStudy">
                  {t("talent:education.fields.field_of_study")} *
                </Label>
                <Input
                  id="fieldOfStudy"
                  value={newEducation.fieldOfStudy}
                  onChange={(e) =>
                    setNewEducation({
                      ...newEducation,
                      fieldOfStudy: e.target.value
                    })
                  }
                  placeholder={t(
                    "talent:education.placeholders.field_of_study"
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  {t("talent:experience.fields.start_date")} *
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newEducation.startDate}
                  onChange={(e) =>
                    setNewEducation({
                      ...newEducation,
                      startDate: e.target.value
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">
                  {t("talent:experience.fields.end_date")} *
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newEducation.endDate || ""}
                  disabled={newEducation.isCurrent}
                  onChange={(e) =>
                    setNewEducation({
                      ...newEducation,
                      endDate: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isCurrent"
                checked={newEducation.isCurrent}
                onCheckedChange={(checked) =>
                  setNewEducation({
                    ...newEducation,
                    isCurrent: checked as boolean,
                    endDate: checked ? null : newEducation.endDate
                  })
                }
              />
              <Label htmlFor="isCurrent">
                {t("talent:education.fields.is_current")}
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {t("talent:education.fields.description")}
              </Label>
              <Textarea
                id="description"
                value={newEducation.description}
                onChange={(e) =>
                  setNewEducation({
                    ...newEducation,
                    description: e.target.value
                  })
                }
                placeholder={t("talent:education.placeholders.description")}
                rows={4}
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              {editingId && (
                <Button variant="outline" onClick={handleCancelEdit}>
                  {t("common:buttons.cancel")}
                </Button>
              )}
              <Button
                id="add-education-button"
                type="button"
                onClick={handleAddEducation}
                className="w-full md:w-auto"
                disabled={!validateNewEducation()}
              >
                <Plus className="w-4 h-4 mr-2" />
                {editingId
                  ? t("common:buttons.save")
                  : t("talent:education.buttons.add")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate("/profile/experience")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common:buttons.back")}
          </Button>
          <Button id="finish-button" onClick={handleSave} disabled={saving}>
            {saving && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            )}
            {t("common:buttons.finish")}
          </Button>
        </div>
      </div>
    </ProfileStepsLayout>
  );
}
